import React, { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, Avatar, Box, CircularProgress, TextField } from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";

export default function UserPicker({
  value,
  onChange,
  disabled,
  label = "Search user",
  requireQuery = false,
}) {
  const token = useSelector((state) => state.auth.token);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const timerRef = useRef(null);

  const labelFor = (u) => {
    if (!u) return "";
    const name = `${u.firstName || ""} ${u.lastName || ""}`.trim();
    return name ? `${name} (${u.email})` : u.email || `User #${u.id}`;
  };

  const fetchUsers = useCallback(
    (search) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        const term = String(search || "").trim();
        if (requireQuery && term.length < 2) {
          setOptions([]);
          setLoading(false);
          return;
        }
        setLoading(true);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/searchUsers?search=${encodeURIComponent(
              search || ""
            )}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.success) {
            setOptions(
              (response.data.data || []).filter((u) => u && u.id != null)
            );
          } else {
            setOptions([]);
          }
        } catch (e) {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 350);
    },
    [token, requireQuery]
  );

  useEffect(() => {
    setInputValue(value ? labelFor(value) : "");
  }, [value]);

  return (
    <Autocomplete
      disabled={disabled}
      options={options}
      value={value || null}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => labelFor(option)}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      inputValue={inputValue}
      onOpen={() => fetchUsers(inputValue)}
      onInputChange={(_, newInput, reason) => {
        setInputValue(newInput);
        if (reason === "input" || reason === "clear") {
          fetchUsers(newInput);
        }
      }}
      onChange={(_, user) => onChange?.(user || null)}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Avatar src={option.profile_img} sx={{ width: 32, height: 32 }} />
          <Box>
            <Box sx={{ fontSize: 13, fontWeight: 600 }}>
              {`${option.firstName || ""} ${option.lastName || ""}`.trim() || "User"}
            </Box>
            <Box sx={{ fontSize: 12, color: "#64748b" }}>{option.email}</Box>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Type name or email..."
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
